// Toggle between 'private' and 'public' visibility on a resource.
// Backed by the PATCH /api/v1/<resource>/:id/visibility endpoints
// added in Phase 1b. Used on document, work-item, and project detail
// pages. Capability-gated by the parent — the button is rendered only
// when the caller has the right `:share` cap (documents:share,
// workitems:share, projects:share).
//
// Visual: a small chip next to the resource title. Private = neutral
// grey + lock glyph. Public = accent color + globe glyph. Hover shows
// the action; click swaps the value.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api.js';

export type Visibility = 'private' | 'public';

interface VisibilityToggleProps {
  /** API path to the resource — e.g. '/workitems', '/documents', '/projects'. */
  resourcePath: string;
  /** Row id. */
  resourceId: string;
  /** Current visibility value from the loaded row. */
  current: Visibility;
  /** What this resource is called in the UI for the tooltip + confirm. */
  resourceLabel: string;
  /** Whether the caller has the share capability. If false, renders
   *  read-only chip with no click handler. */
  canShare: boolean;
  /** Query keys to invalidate after a successful toggle (so the parent
   *  view reflects the new state without a manual refetch). */
  invalidateKeys?: ReadonlyArray<ReadonlyArray<unknown>>;
}

export function VisibilityToggle({
  resourcePath,
  resourceId,
  current,
  resourceLabel,
  canShare,
  invalidateKeys = [],
}: VisibilityToggleProps) {
  const qc = useQueryClient();
  const [hovered, setHovered] = useState(false);

  const mutate = useMutation({
    mutationFn: (next: Visibility) =>
      apiFetch(`${resourcePath}/${resourceId}/visibility`, {
        method: 'PATCH',
        body: { visibility: next },
      }),
    onSuccess: () => {
      for (const key of invalidateKeys) {
        qc.invalidateQueries({ queryKey: key as unknown[] });
      }
    },
  });

  const isPublic = current === 'public';
  const accent = isPublic ? 'var(--accent-primary)' : 'var(--text-muted)';
  const glyph = isPublic ? '🌐' : '🔒';
  const labelNow = isPublic ? 'Public' : 'Private';
  const labelNext = isPublic ? 'Make private' : 'Make public';

  // Read-only chip when the caller can't share.
  if (!canShare) {
    return (
      <span
        title={`This ${resourceLabel} is ${labelNow.toLowerCase()}. You don't have permission to change it.`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          fontSize: 11,
          border: `1px solid ${isPublic ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
          borderRadius: 4,
          color: accent,
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span>{glyph}</span>
        <span>{labelNow.toLowerCase()}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        if (mutate.isPending) return;
        if (isPublic) {
          // Going public→private is safe (narrows visibility). Always
          // act without confirm.
          mutate.mutate('private');
        } else {
          // Going private→public widens reach to every other org the
          // caller's a member of. Confirm so it doesn't happen by
          // accident.
          if (window.confirm(
            `Make this ${resourceLabel} visible to every organization you're a member of? ` +
            `Other orgs will be able to read it but not edit it.`,
          )) {
            mutate.mutate('public');
          }
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={mutate.isPending}
      title={mutate.isPending ? 'Updating…' : labelNext}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        fontSize: 11,
        background: hovered ? 'var(--surface-sunken)' : 'transparent',
        border: `1px solid ${isPublic ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
        borderRadius: 4,
        color: accent,
        fontFamily: 'var(--font-mono)',
        cursor: mutate.isPending ? 'wait' : 'pointer',
        transition: 'background 80ms',
      }}
    >
      <span>{glyph}</span>
      <span>{hovered ? labelNext.toLowerCase() : labelNow.toLowerCase()}</span>
    </button>
  );
}

// ─── "Shared from {Org}" badge ────────────────────────────────────────────
//
// Rendered next to titles in lists/detail when the resource's orgId
// differs from the caller's active org. Signals "you're seeing this
// via Phase-1c visibility=public, you can't edit it from here".

export function SharedFromBadge({ ownerOrgName, ownerOrgSlug }: { ownerOrgName: string; ownerOrgSlug?: string }) {
  return (
    <span
      title={`This is a public resource owned by another organization (${ownerOrgSlug ?? ownerOrgName}). You can read it but not edit it from your current org.`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        fontSize: 11,
        background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-raised))',
        border: '1px dashed color-mix(in srgb, var(--accent-primary) 40%, var(--border-strong))',
        borderRadius: 4,
        color: 'var(--accent-primary)',
        fontFamily: 'var(--font-mono)',
      }}
    >
      <span>↗</span>
      <span>shared from {ownerOrgName}</span>
    </span>
  );
}
